// NOTE: imported from the concrete module rather than the `../types` barrel —
// the barrel re-exports files that import back from `../constants`, so going
// through it would close a constants -> types -> constants loop. It is
// type-only and would be erased, but naming the file keeps the cycle from
// existing at all.
// NOTE: A VALUE import (enums), unlike the type-only one below — also from the
// concrete module rather than the `../types` barrel, and safe because
// types/integration.ts imports nothing from `../constants`, so no cycle exists.
import { EMcpAuthMode, EMcpTransport } from "../types/integration";
import type {
  IOrganization,
  IOrganizationRole,
  TSamlSeatTierLimits,
} from "../types/organizations";
import { ESearchEmbeddingModelIssue } from "../types/organizations";

export const ORGANIZATIONS_QUERY_KEY = "organizations";

export const ORG_PROJECTS_QUERY_KEY = "organizationProjects";

export const ORG_ROLES_QUERY_KEY = "organizationRoles";

export const ORG_ACCESS_MATRIX_QUERY_KEY = "organizationAccessMatrix";

export const ORG_MODELS_QUERY_KEY = "organizationModels";

export const ORG_MODEL_USAGE_QUERY_KEY = "organizationModelUsage";

export const ORG_ASSISTANTS_QUERY_KEY = "organizationAssistants";

export const ORG_MEMBERS_QUERY_KEY = "organizationMembers";

// NOTE: NSAI-5664 — whether the selected search embedding model is in effect.
export const ORG_SEARCH_EMBEDDING_STATUS_QUERY_KEY =
  "organizationSearchEmbeddingStatus";

// NOTE: NSAI-5664 — the `settings` namespace key each client shows under the
// search embedding model when the selection is not in effect.
export const SEARCH_EMBEDDING_MODEL_ISSUE_MESSAGE_KEYS = {
  [ESearchEmbeddingModelIssue.NOT_ELIGIBLE]:
    "chat_settings.search_embedding_model.status_unusable_not_eligible",
  [ESearchEmbeddingModelIssue.PROVIDER_ERROR]:
    "chat_settings.search_embedding_model.status_unusable_provider_error",
  [ESearchEmbeddingModelIssue.DIMENSION_MISMATCH]:
    "chat_settings.search_embedding_model.status_unusable_dimension_mismatch",
} as const satisfies Record<ESearchEmbeddingModelIssue, string>;

// NOTE: NSAI-6515 — the owner-only list of people banned from an organization.
export const ORG_BANNED_MEMBERS_QUERY_KEY = "organizationBannedMembers";

export const ORG_SYNCATOR_KEYS_QUERY_KEY = "organizationSyncatorKeys";

export const ORG_PROBE_MODELS_QUERY_KEY = "organizationProbeModels";

export const PROBE_ELIGIBLE_MODELS_QUERY_KEY = "probe-eligible-models";

export const ORG_USAGE_LIMITS_QUERY_KEY = "organizationUsageLimits";

export const ORG_USAGE_LIMITS_OVERVIEW_QUERY_KEY =
  "organizationUsageLimitsOverview";

// NOTE: NSAI-5037 — per-model request caps. A separate key from the usage-limits
// one so saving a period budget does not refetch the model matrix (and vice
// versa); the two endpoints are independent.
export const ORG_MODEL_TOKEN_LIMITS_QUERY_KEY = "organizationModelTokenLimits";

export const ORG_VERIFIED_DOMAINS_QUERY_KEY = "organizationVerifiedDomains";

// NOTE: SAML provisioning-mode options, shared by the web radio cards and the
// mobile SAML tab (NSAI-5345) so the two clients cannot drift on the mode
// list. `as const` keeps the i18n keys as literals (so the typed `t()` accepts
// them) and pins `value` to the literal provisioning modes — a rename surfaces
// as a compile error at the usage site. Icons stay platform-side: this list
// must remain JSX-free.
export const PROVISIONING_MODE_OPTIONS = [
  {
    value: "INVITE_ONLY",
    labelKey: "organizationSaml.form.provisioningModeInviteOnly",
    descriptionKey:
      "organizationSaml.form.provisioningModeInviteOnlyDescription",
    testId: "organization-saml-provisioning-mode-invite-only",
  },
  {
    value: "JIT",
    labelKey: "organizationSaml.form.provisioningModeJit",
    descriptionKey: "organizationSaml.form.provisioningModeJitDescription",
    testId: "organization-saml-provisioning-mode-jit",
  },
] as const;

export const ORG_INVITES_QUERY_KEY = "organizationInvites";

export const ORG_SIEM_CONFIG_QUERY_KEY = "organizationSiemConfig";

export const DEFAULT_MCP_CONFIG = {
  title: "",
  url: "",
  isRequiredToken: false,
  // NOTE: NSAI-5354 — AUTO by default so a new server does not have to be
  // diagnosed by hand: the backend probes Streamable HTTP then SSE and records
  // whichever answered.
  //
  // NONE, not BEARER, and deliberately so: a blank row has no token and no
  // "members supply their own" either, which is exactly what the runtime resolved
  // to NONE before this ticket added an explicit scheme — so NONE is what a blank
  // config has always MEANT. BEARER also made the default row invalid against the
  // schema's own rule (a static scheme needs a credential from somewhere), which
  // silently blocked `Add` until the owner discovered they had to tick the token
  // switch first. The owner picks a real scheme once they know the server.
  transport: EMcpTransport.AUTO,
  authMode: EMcpAuthMode.NONE,
  authHeaderName: "",
  token: "",
  isUsedBySpecificModels: false,
  specificModelUuids: [] as string[],
  isUsedBySpecificAssistant: false,
  specificAssistantUuids: [] as string[],
  // NOTE: NSAI-5354 — folded in from web's own divergent copy of this constant.
  // The two had drifted (web had the project fields, this one did not), which is
  // the same class of split that made NSAI-5292 a 400 on mobile. One default now
  // seeds a new MCP row on every platform.
  isUsedBySpecificProjects: false,
  projectUuids: [] as string[],
};

// NOTE: NSAI-4567 — stable empty results for the organization queries, same
// mechanism as `EMPTY_ASSISTANTS`. A `= []` destructuring default allocates a
// fresh array on every render for as long as the query has no data: while it
// is in flight, and permanently when the caller's role cannot read the
// resource. Anything keyed on that value therefore sees a new identity every
// render, and an effect that writes state then re-renders without bound
// ("Maximum update depth exceeded").
//
// `EMPTY_ORGANIZATION_ROLES` is the one with a demonstrated loop behind it: the
// mobile SAML tab keys an unconditional `form.reset()` effect on the roles and
// projects lists. `EMPTY_ORGANIZATIONS` is hardening for the organization
// provider, whose defaulted lists reach the members and invite effects through
// context.
//
// Not frozen on purpose — callers pass these into props typed as mutable
// arrays, and a runtime freeze would turn a today-harmless in-place use into a
// throw.
export const EMPTY_ORGANIZATIONS: IOrganization[] = [];

export const EMPTY_ORGANIZATION_ROLES: IOrganizationRole[] = [];

// NOTE: Same stable-identity reason as the lists above, for the SAML seat-tier
// usage map: it feeds both the memoized seat-tier panel and the zod schema that
// validates the per-tier caps against it (NSAI-4936), so a fresh `{}` each
// render would rebuild the resolver and defeat the memo for every org whose
// usage is absent (feature off, or a non-owner reader).
export const EMPTY_SAML_SEAT_TIER_USAGE: TSamlSeatTierLimits = {};

// NOTE: NSAI-5354 — query key for the owner-only listing of MCP servers members
// connected themselves. Shared so web and mobile invalidate the same cache after
// an adopt or a remove.
export const ORG_MCP_CONNECTIONS_QUERY_KEY = "organizationMcpConnections";

// NOTE: NSAI-6903 — the most minutes before a calendar meeting starts that an
// organization can have the auto-joined bot click Join. Mirrors the server's
// MAX_BOT_JOIN_MINUTES_BEFORE_START: the bot is dispatched only this far ahead
// of the meeting, so it cannot wait for longer.
export const MAX_BOT_JOIN_MINUTES_BEFORE_START = 5;

// NOTE: NSAI-6903 — the picker value for a join time that is not set (null):
// the bot asks to join as soon as it is ready. Pickers hold strings, and a
// Radix select item cannot carry "".
export const BOT_JOIN_TIME_NOT_SET_VALUE = "not_set";
